/**
 * Drenado de la cola offline hacia Supabase.
 *
 * Distingue dos fallos que casi todas las apps confunden:
 *
 *   - RED CAIDA  -> el score sigue en la cola y se reintenta. Es normal.
 *   - RECHAZO    -> el servidor dijo que no (permisos, WOD bloqueado,
 *                   score ya validado). Reintentarlo mil veces no lo va a
 *                   arreglar: se marca y se le muestra al juez.
 */

import { listQueued, markAttempt, removeQueued, type QueuedScore } from './queue.js';
import { supabase } from './supabase.js';

export interface SyncResult {
  sent: number;
  rejected: number;
  pending: number;
}

/** Codigos de PostgREST que significan "no lo intentes otra vez". */
const PERMANENT_CODES = new Set([
  '42501', // insufficient_privilege -- lo bloqueo una politica RLS
  '23503', // foreign_key_violation
  '23514', // check_violation
  '22P02', // invalid_text_representation
  'P0001', // raise exception desde un trigger
]);

function isPermanent(error: { code?: string; message?: string }): boolean {
  if (error.code && PERMANENT_CODES.has(error.code)) return true;
  // PostgREST devuelve 401/403 sin codigo SQL cuando RLS niega la fila.
  return Boolean(error.message && /permission|policy|row-level security/i.test(error.message));
}


/**
 * Traduce el rechazo del servidor a algo que el juez entienda.
 *
 * Un 'new row violates row-level security policy' en mitad de una
 * competencia no le dice nada a nadie. El caso frecuente tiene nombre
 * propio y merece su frase.
 */
function motivo(error: { code?: string; message?: string }): string {
  const m = error.message ?? '';
  if (/row-level security|policy|permission/i.test(m)) {
    return (
      'El servidor rechazo este score. Si es tu propia inscripcion, no puedes ' +
      'puntuarte a ti mismo: que lo meta otro juez.'
    );
  }
  return m || 'El servidor rechazo el score';
}

async function send(entry: QueuedScore): Promise<'sent' | 'rejected' | 'retry'> {
  const { error } = await supabase
    .from('scores')
    .upsert(entry.payload, { onConflict: 'workout_id,registration_id' });

  if (!error) return 'sent';

  if (isPermanent(error)) {
    await markAttempt(entry.key, motivo(error));
    return 'rejected';
  }

  await markAttempt(entry.key);
  return 'retry';
}

let running = false;

/**
 * Drena la cola. Es seguro llamarla en paralelo: si ya hay un drenado en
 * curso, la segunda llamada no hace nada.
 */
export async function drainQueue(): Promise<SyncResult> {
  if (running || !navigator.onLine) {
    const pending = await listQueued();
    return { sent: 0, rejected: 0, pending: pending.length };
  }

  running = true;
  let sent = 0;
  let rejected = 0;

  try {
    const queued = await listQueued();
    for (const entry of queued) {
      // Un rechazo ya notificado no se reintenta solo.
      if (entry.rejectedReason) {
        rejected++;
        continue;
      }
      const outcome = await send(entry);
      if (outcome === 'sent') {
        await removeQueued(entry.key);
        sent++;
      } else if (outcome === 'rejected') {
        rejected++;
      } else {
        // Se corto la red a mitad del drenado: parar y esperar.
        break;
      }
    }
  } finally {
    running = false;
  }

  const pending = await listQueued();
  return { sent, rejected, pending: pending.length };
}

/** Reintenta a mano un score que el servidor habia rechazado. */
export async function retryRejected(key: string): Promise<void> {
  await markAttempt(key, undefined);
  await drainQueue();
}
